import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Download,
  Plus,
  CheckCircle2,
  Lock,
  Loader2,
  AlertCircle,
  ArrowLeft,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  fetchEvaluationDetails,
  evaluationDetailSelector,
} from "./evaluationDetailSlice";

import Markdown from "react-markdown";

// Custom components for React Markdown to ensure consistent styling
const MarkdownComponents = {
  strong: ({ node, ...props }: any) => (
    <span className="font-bold text-gray-900" {...props} />
  ),
  b: ({ node, ...props }: any) => (
    <span className="font-bold text-gray-900" {...props} />
  ),
  ul: ({ node, ...props }: any) => (
    <ul className="space-y-1.5 mb-2 list-disc list-outside ml-4" {...props} />
  ),
  ol: ({ node, ...props }: any) => (
    <ol className="space-y-1.5 mb-2 list-decimal list-outside ml-4" {...props} />
  ),
  li: ({ node, ...props }: any) => (
    <li className="text-sm text-gray-600 pl-1 leading-normal" {...props} />
  ),
  p: ({ node, ...props }: any) => (
    <div className="text-sm text-gray-600 leading-normal mb-1.5 inline-block" {...props} />
  ),
  a: ({ node, ...props }: any) => (
    <a className="text-indigo-600 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
  ),
};

const MarkdownRenderer = ({ content, className = "" }: { content: string; className?: string }) => {
  if (!content) return null;

  // Handle escaped newlines if present
  const normalizedContent = content.replace(/\\n/g, '\n');

  return (
    <div className={`markdown-content ${className}`}>
      <Markdown components={MarkdownComponents}>{normalizedContent}</Markdown>
    </div>
  );
};


interface EssaySection {
  key_strengths: string[];
  areas_for_improvement: string[];
}

interface EssaySections {
  understanding_of_topic?: EssaySection;
  introduction_competence?: EssaySection;
  coverage_of_dimensions?: EssaySection;
  conclusion_competence?: EssaySection;
  flow_of_content?: EssaySection;
  language_competence?: EssaySection;
  examples_facts_current_affairs?: EssaySection;
}

interface Question {
  id: number;
  question_id: string;
  question_text: string;
  question_number: string;
  answer_text: string;
  score: number;
  maxScore: number;
  wordCount: number;
  keyStrengths: string[];
  areasForImprovement?: {
    introduction?: string[];
    body?: string[];
    conclusion?: string[];
  };
  modelAnswer?: string | null;
  hints?: string | null;
  sections?: EssaySections;
}

interface EvaluationData {
  fileName: string;
  date: string;
  feedback: Question[];
  overallFeedback: string;
  overallScore: number;
  maxScore: number;
  paper: string;
  evaluationId: string;
  s3url?: string;
}

const EvaluationDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { isFetchingDetails } = useAppSelector(evaluationDetailSelector);

  const [evaluationData, setEvaluationData] = useState<EvaluationData | null>(
    null
  );
  const [selectedQuestion, setSelectedQuestion] = useState<number | null>(null);
  const [showMoreAnswer, setShowMoreAnswer] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch evaluation details when component mounts or id changes
  useEffect(() => {
    if (id) {
      setIsLoading(true);
      setError(null);
      dispatch(fetchEvaluationDetails(id))
        .unwrap()
        .then((data) => {
          // Map API response to component state
          const evaluations = data.evaluations || [];
          const mappedQuestions: Question[] = evaluations.map(
            (evaluation: any, index: number) => ({
              id: index + 1,
              question_id: evaluation.question_id,
              question_text: evaluation.question_text || "",
              question_number: evaluation.question_number || `Q${index + 1}`,
              answer_text: evaluation.answer_text || "",
              score: parseFloat(evaluation.score) || 0,
              maxScore: parseFloat(evaluation.max_score) || 0,
              wordCount: parseInt(evaluation.word_count) || 0,
              keyStrengths: evaluation.key_strengths_of_answer?.strength || [],
              areasForImprovement: {
                introduction:
                  evaluation.areas_for_improvement?.introduction || [],
                body: evaluation.areas_for_improvement?.body || [],
                conclusion: evaluation.areas_for_improvement?.conclusion || [],
              },
              modelAnswer: evaluation.model_answer,
              hints: evaluation.hints,
              sections: evaluation.sections || null,
            })
          );

          const mappedData: EvaluationData = {
            fileName: data.paper || "Answer Sheet",
            date: new Date().toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            }),
            feedback: mappedQuestions,
            overallFeedback: data.overall_feedback || "",
            overallScore: parseFloat(data.overall_score) || 0,
            maxScore: parseFloat(data.max_score) || 0,
            paper: data.paper || "",
            evaluationId: data.evaluation_id || id,
            s3url: data.s3url,
          };

          setEvaluationData(mappedData);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("❌ Error fetching evaluation details:", err);
          setError(err?.message || "Failed to load evaluation details");
          setIsLoading(false);
          toast.error(err?.message || "Failed to load evaluation details");
        });
    } else {
      setError("Evaluation ID not found in URL");
      setIsLoading(false);
    }
  }, [id, dispatch]);

  const isScrollingRef = useRef(true); // Start as true to prevent observer on initial load

  useEffect(() => {
    window.scrollTo(0, 0);
    setSelectedQuestion(null); // Ensure Overall is selected on load
    // Enable observer after initial scroll completes
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 500);
  }, []);

  // Handle button click - scroll to section
  const handleQuestionClick = (questionId: number | null) => {
    isScrollingRef.current = true; // Disable observer during programmatic scroll
    setSelectedQuestion(questionId);

    const elementId = questionId === null ? "overall-feedback" : `question-${questionId}`;
    const element = document.getElementById(elementId);
    if (element) {
      // Adjusted offset for global header + extra buffer
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }

    // Re-enable observer after scroll completes
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 1000);
  };

  // Track which section is visible while scrolling
  useEffect(() => {
    if (!evaluationData) return;

    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -60% 0px", // Adjusted trigger zone
      threshold: 0,
    };

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      if (isScrollingRef.current) return; // Ignore during programmatic scroll

      // Find the entry that's most in view (topmost visible element)
      let bestEntry: IntersectionObserverEntry | undefined;

      for (const entry of entries) {
        if (entry.isIntersecting) {
          bestEntry = entry;
          break; // Take the first intersecting one from top
        }
      }

      if (bestEntry) {
        const id = (bestEntry.target as HTMLElement).id;
        if (id === "overall-feedback") {
          setSelectedQuestion(null);
        } else if (id.startsWith("question-")) {
          const questionId = parseInt(id.replace("question-", ""));
          setSelectedQuestion(questionId);
        }
      }
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    // Observe overall feedback
    const overallElement = document.getElementById("overall-feedback");
    if (overallElement) observer.observe(overallElement);

    // Observe all question cards
    evaluationData.feedback.forEach((q) => {
      const element = document.getElementById(`question-${q.id}`);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [evaluationData]);

  const handleDownloadReport = () => {
    if (evaluationData?.s3url) {
      window.open(evaluationData.s3url, "_blank");
      toast.success("Report downloaded successfully!");
    } else {
      toast.error("Report URL not available");
    }
  };

  const handleEvaluateNew = () => {
    navigate("/evaluate");
  };


  const toggleShowMoreAnswer = (qId: number) => {
    setShowMoreAnswer((prev) => {
      const next = new Set(prev);
      if (next.has(qId)) {
        next.delete(qId);
      } else {
        next.add(qId);
      }
      return next;
    });
  };

  // Loading state
  if (isLoading || isFetchingDetails) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container max-w-7xl mx-auto p-4 md:p-8">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center space-y-4">
              <Loader2 className="w-10 h-10 animate-spin text-gray-900 mx-auto" />
              <p className="text-gray-500 font-medium">Loading evaluation...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !evaluationData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Evaluation</h2>
          <p className="text-gray-500 mb-6">{error || "Evaluation details could not be found."}</p>
          <Button onClick={() => navigate("/history")} className="bg-black text-white hover:bg-gray-800">
            Back to History
          </Button>
        </div>
      </div>
    );
  }

  const totalScore = evaluationData.feedback.reduce(
    (acc, q) => acc + q.score,
    0
  );
  const overallScore = evaluationData.overallScore || totalScore;
  const maxTotalScore = evaluationData.maxScore || 0;

  // Helper for score color
  // Helper for score color
  const getScoreColor = (score: number, max: number) => {
    const percentage = (score / max) * 100;
    if (percentage <= 30) return "text-red-600";
    if (percentage <= 50) return "text-yellow-600";
    return "text-green-600";
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 selection:bg-purple-100 selection:text-purple-900">

      {/* Header */}
      <div className="bg-white/90 backdrop-blur-xl border-b border-gray-200 sticky top-0 z-50 transition-all duration-200">
        <div className="container mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-2">

          {/* Left: Navigation & Context */}
          <div className="flex items-center gap-2 md:gap-5 min-w-0 flex-1">
            <button
              onClick={() => navigate('/history')}
              className="group shrink-0 p-2 rounded-full bg-gray-50 hover:bg-black hover:text-white text-gray-500 transition-all duration-300"
              title="Back to History"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2 md:gap-3">
                <h1 className="text-sm md:text-base font-extrabold text-gray-900 tracking-tight truncate">Evaluation Report</h1>
                <span className="hidden sm:block h-1 w-1 rounded-full bg-gray-300 shrink-0"></span>
                <span className="hidden sm:block text-sm font-medium text-gray-600 truncate max-w-[120px] md:max-w-[200px]">{evaluationData.paper}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium truncate mt-0.5">
                <span className="sm:hidden truncate max-w-[100px]">{evaluationData.paper}</span>
                <span className="sm:hidden shrink-0">•</span>
                <span className="shrink-0">Powered by Kuberiya AI</span>
              </div>
            </div>
          </div>

          {/* Right: Score & Actions */}
          <div className="flex items-center gap-2 md:gap-6 shrink-0">

            {/* Score Display */}
            <div className="flex flex-col items-end mr-1">
              <span className="text-[8px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-0.5">Score</span>
              <div className="flex items-baseline gap-0.5">
                <span className={`text-lg md:text-2xl font-bold leading-none ${getScoreColor(overallScore, maxTotalScore)}`}>{overallScore.toFixed(1)}</span>
                <span className="text-[10px] md:text-sm font-bold text-indigo-400">/{maxTotalScore}</span>
              </div>
            </div>

            <div className="h-6 w-px bg-gray-200 hidden md:block" />

            <div className="flex gap-2">
              {/* New Eval */}
              <Button onClick={handleEvaluateNew} className="bg-indigo-500 text-white hover:bg-indigo-800 h-8 w-8 md:h-10 md:w-auto md:px-5 px-3 rounded-full shadow-lg shadow-gray-200/50 transition-all active:scale-95 flex items-center justify-center">
                <Plus className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline text-sm font-bold">New Evaluation</span>
              </Button>

              {/* Download */}
              <Button variant="outline" onClick={handleDownloadReport} className=" text-black h-8 w-8 md:h-10 md:w-auto md:px-5 px-3 rounded-full shadow-lg shadow-gray-200/50 transition-all active:scale-95 flex items-center justify-center">
                <span className="hidden md:inline text-sm font-bold">Download</span>  <Download className="w-4 h-4 md:mr-2" />
              </Button>
            </div>
          </div>

        </div>
      </div>

      <div className="container mx-auto px-1 md:px-8 py-8 md:py-12 flex flex-col lg:flex-row gap-12 relative">

        {/* Main Content (Left Side) */}
        <main className="flex-1 min-w-0 space-y-8">

          {/* Questions List */}
          <div className="space-y-8">
            {evaluationData.feedback.map((question) => {
              const isAnswerExpanded = showMoreAnswer.has(question.id);
              const maxTruncatedLength = 300;

              return (
                <div key={question.id} id={`question-${question.id}`} className="scroll-mt-32">
                  {/* Question Header / Meta */}
                  <div className="flex items-end gap-4 mb-3 px-2">
                    <span className="text-sm font-bold text-indigo-400 uppercase tracking-widest">Question {question.question_number}</span>
                    <div className="flex-1 h-px bg-gray-200 mb-1.5" />
                    <span className={`text-sm font-bold ${getScoreColor(question.score, question.maxScore)}`}>
                      Score: {question.score.toFixed(1)} / {question.maxScore}
                    </span>
                  </div>

                  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden p-3 md:p-6 transition-shadow hover:shadow-md">

                    {/* Question Text */}
                    <div className="mb-3">
                      <h3 className="text-lg flex md:flex-row items-start gap-2 flex-col justify-start font-bold text-indigo-900 leading-snug font-sans">
                        <span className="text-indigo-500 text-sm">Ques.</span>
                        <div className="text-gray-600 flex-1">
                          <MarkdownRenderer content={question.question_text} />
                        </div>
                      </h3>
                    </div>

                    {/* Answer Section */}
                    <div className="mb-3 relative">
                      <div className="absolute top-0 left-0 bottom-0 w-1 bg-indigo-400 rounded-full" />
                      <div className="pl-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-gray-400 uppercase tracking-wider text-[10px]">Your Answer</span>
                          <span className="text-[10px] text-gray-400">{question.wordCount} words</span>
                        </div>
                        <div className="text-gray-600 text-sm leading-normal">
                          <MarkdownRenderer content={isAnswerExpanded || question.answer_text.length <= maxTruncatedLength
                            ? question.answer_text
                            : `${question.answer_text.slice(0, maxTruncatedLength)}...`
                          } />
                        </div>
                        {question.answer_text.length > maxTruncatedLength && (
                          <button
                            onClick={() => toggleShowMoreAnswer(question.id)}
                            className="mt-1 text-xs font-bold text-indigo-500 hover:text-indigo-600 transition-colors inline-flex items-center gap-1"
                          >
                            {isAnswerExpanded ? "Read Less" : "Read Full Answer"}
                            <ChevronDown className={`w-3 h-3 transition-transform ${isAnswerExpanded ? 'rotate-180' : ''}`} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Feedback Section */}
                    <div className="bg-gray-50 rounded-2xl p-3 md:p-4 space-y-3">
                      {/* Essay Sections - Show when sections exist */}
                      {question.sections && Object.keys(question.sections).length > 0 ? (
                        <div className="space-y-3">
                          {Object.entries(question.sections).map(([sectionKey, sectionData], sectionIndex) => {
                            if (!sectionData) return null;
                            const sectionTitle = sectionKey
                              .split('_')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ');

                            return (
                              <div key={sectionKey} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                {/* Section Header */}
                                <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-4 py-2">
                                  <h4 className="text-xs font-bold text-white flex items-center gap-2">
                                    <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-white text-[10px] font-bold">
                                      {sectionIndex + 1}
                                    </span>
                                    <span className="uppercase tracking-wide">{sectionTitle}</span>
                                  </h4>
                                </div>

                                <div className="p-2.5 space-y-2">
                                  {/* Key Strengths */}
                                  {sectionData.key_strengths && sectionData.key_strengths.length > 0 && (
                                    <div className="bg-emerald-50 rounded p-2.5 border-l-4 border-emerald-500">
                                      <h5 className="text-xs font-bold text-emerald-700 flex items-center gap-1.5 mb-1.5">
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        KEY STRENGTHS
                                      </h5>
                                      <ul className="space-y-1.5">
                                        {sectionData.key_strengths.map((strength: string, i: number) => (
                                          <li key={i} className="text-sm text-gray-700 flex items-start gap-2 leading-normal">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-[6px] flex-shrink-0"></span>
                                            <span className="flex-1"><MarkdownRenderer content={strength} /></span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Areas for Improvement */}
                                  {sectionData.areas_for_improvement && sectionData.areas_for_improvement.length > 0 && (
                                    <div className="bg-amber-50 rounded p-2.5 border-l-4 border-amber-500">
                                      <h5 className="text-xs font-bold text-amber-700 flex items-center gap-1.5 mb-1.5">
                                        <AlertCircle className="w-3.5 h-3.5" />
                                        AREAS FOR IMPROVEMENT
                                      </h5>
                                      <ul className="space-y-1.5">
                                        {sectionData.areas_for_improvement.map((improvement: string, i: number) => (
                                          <li key={i} className="text-sm text-gray-700 flex items-start gap-2 leading-normal">
                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-[6px] flex-shrink-0"></span>
                                            <span className="flex-1"><MarkdownRenderer content={improvement} /></span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <>
                          {/* Regular Format - Strengths */}
                          {question.keyStrengths && question.keyStrengths.length > 0 && (
                            <div>
                              <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-2">
                                <div className="p-1 rounded bg-emerald-100 text-emerald-700">
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                </div>
                                Key Strengths
                              </h4>
                              <ul className="space-y-1.5 bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                                {question.keyStrengths.map((str, i) => (
                                  <li key={i} className="text-sm text-gray-600 flex gap-2 items-start leading-normal">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-[6px] flex-shrink-0"></span>
                                    <span className="flex-1"><MarkdownRenderer content={str} /></span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Regular Format - Improvements */}
                          {(question.areasForImprovement?.introduction?.length || question.areasForImprovement?.body?.length || question.areasForImprovement?.conclusion?.length) ? (
                            <div>
                              <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-2">
                                <div className="p-1 rounded bg-amber-100 text-amber-700">
                                  <AlertCircle className="w-3.5 h-3.5" />
                                </div>
                                Areas for Improvement
                              </h4>
                              <div className="space-y-3 bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                                {['introduction', 'body', 'conclusion'].map((section) => {
                                  // @ts-ignore
                                  const items = question.areasForImprovement?.[section];
                                  if (!items || items.length === 0) return null;
                                  return (
                                    <div key={section}>
                                      <h5 className="text-xs font-bold text-indigo-500 uppercase tracking-wide mb-1.5">{section}</h5>
                                      <ul className="space-y-1.5">
                                        {items.map((item: string, i: number) => (
                                          <li key={i} className="text-sm text-gray-600 flex gap-2 items-start leading-normal">
                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-[6px] flex-shrink-0"></span>
                                            <span className="flex-1"><MarkdownRenderer content={item} /></span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>

                    {/* Model Answer Toggle */}
                    {question.modelAnswer && (
                      <div className="mt-6 flex justify-center">
                        <button className="group flex flex-col items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-widest hover:text-gray-900 transition-colors">
                          <div className="w-10 h-10 rounded-full border-2 border-dashed border-gray-200 flex items-center justify-center group-hover:border-gray-900 group-hover:bg-gray-50 transition-all">
                            <Lock className="w-4 h-4" />
                          </div>
                          <span>Unlock Model Answer</span>
                        </button>
                      </div>
                    )}

                  </div>
                </div>
              );
            })}
          </div>
        </main>

        {/* Sidebar Navigation (Right Side in lg screens) */}
        <aside className="hidden lg:block w-80 flex-shrink-0">
          <div className="sticky top-28 pl-4 max-h-[calc(100vh-8rem)] flex flex-col">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6 px-4 flex-shrink-0">Contents</h3>

            {/* Timeline Track */}
            <div className="ml-4 space-y-0 text-sm overflow-y-auto flex-1 pb-4">
              {evaluationData.feedback.map((q, idx) => {
                // Determine active state: Default to first question if null
                const currentIdx = selectedQuestion ? evaluationData.feedback.findIndex(i => i.id === selectedQuestion) : 0;
                const isActive = idx === currentIdx;
                const isPast = idx < currentIdx;
                const isLast = idx === evaluationData.feedback.length - 1;

                return (
                  <div key={q.id} className="group relative flex gap-4 pb-2">
                    {/* Timeline Column */}
                    <div className="flex flex-col items-center flex-shrink-0 w-6 relative pt-4">
                      {/* Vertical Line */}
                      {!isLast && (
                        <div className={`absolute top-8 bottom-[-8px] w-0.5 transition-colors duration-300 ${isPast ? "bg-indigo-600" : "bg-gray-200"
                          }`} />
                      )}

                      {/* Dot Indicator */}
                      <div className={`relative z-10 rounded-full border-2 transition-all duration-300 ${isActive
                        ? "w-5 h-5 bg-indigo-600 border-indigo-600 ring-4 ring-indigo-100"
                        : isPast
                          ? "w-3 h-3 bg-indigo-600 border-indigo-600 mt-1"
                          : "w-3 h-3 bg-white border-gray-300 group-hover:border-gray-400 mt-1"
                        }`} />
                    </div>

                    {/* Content Card */}
                    <button
                      onClick={() => handleQuestionClick(q.id)}
                      className={`text-left flex-1 rounded-xl p-3 transition-all duration-300 ${isActive
                        ? "bg-white shadow-sm ring-1 ring-black/5"
                        : "hover:bg-gray-50"
                        }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex flex-col gap-1 min-w-0">
                          <span className={`text-sm font-bold truncate transition-colors ${isActive ? "text-indigo-900" : isPast ? "text-indigo-700" : "text-gray-500 group-hover:text-gray-900"
                            }`}>
                            Question {q.question_number}
                          </span>
                          <span className={`text-xs font-medium truncate ${isActive || isPast ? "text-indigo-400" : "text-gray-400"
                            }`}>
                            {q.wordCount} words
                          </span>
                        </div>
                        <div className={`shrink-0 px-2 py-1 rounded-md text-xs font-bold transition-colors ${isActive || isPast
                          ? `bg-indigo-50 border border-indigo-100 ${getScoreColor(q.score, q.maxScore)}`
                          : "bg-gray-100 text-gray-500 border border-gray-200"
                          }`}>
                          {q.score.toFixed(1)}/{q.maxScore}
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </aside>

      </div>
    </div>
  );
};

export default EvaluationDetail;
